import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Button,
  DialogActions,
  Autocomplete,
  Chip,
} from '@mui/material';
import { useState } from 'react';
import { SimulationDataSetDto, SimulationDataSetUpdateRequest } from '@/api/ApiClient';
import { FormLabel } from '@/components/common/FormLabel';

type ReaxffEditDialogProps = {
  isEditModalOpen: boolean;
  setIsEditModalOpen: (open: boolean) => void;
  editingItem: SimulationDataSetDto | null;
  handleSave: (data: SimulationDataSetUpdateRequest) => Promise<void>;
};

export function ReaxffEditDialog({
  editingItem,
  isEditModalOpen,
  setIsEditModalOpen,
  handleSave,
}: ReaxffEditDialogProps) {
  // Use function initializer for initial values - key prop will force remount when editingItem changes
  const [author, setAuthor] = useState(() => editingItem?.mainAuthor ?? '');
  const [authorError, setAuthorError] = useState<string | null>(null);
  const [elements, setElements] = useState(() => editingItem?.elements.split(',') || []);
  const [elementsError, setElementsError] = useState<string | null>(null);
  const [year, setYear] = useState(() => editingItem?.year ?? 0);
  const [doi, setDoi] = useState(() => editingItem?.doi ?? '');
  const [inputValue, setInputValue] = useState('');

  const validateFields = () => {
    let isValid = true;
    if (author.trim() === '') {
      setAuthorError('Author is required');
      isValid = false;
    }
    if (elements.length === 0) {
      setElementsError('At least one element is required');
      isValid = false;
    } else {
      setElementsError(null);
    }
    return isValid;
  };

  return (
    <Dialog key={editingItem?.id ?? 'new'} open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
      <DialogTitle>
        Edit Metadata for <i>{editingItem?.filename ?? 'N/A'}</i>
      </DialogTitle>
      <DialogContent>
        {editingItem ? (
          <>
            <Box>
              <FormLabel required>Author</FormLabel>
              <TextField
                fullWidth
                value={author}
                margin="normal"
                onChange={(e) => {
                  setAuthor(e.target.value);
                  if (e.target.value.trim() === '') {
                    setAuthorError('Author is required');
                  } else {
                    setAuthorError(null);
                  }
                }}
                error={!!authorError}
                helperText={authorError}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <FormLabel required>Elements</FormLabel>
              <Autocomplete
                multiple
                freeSolo
                options={[]} // empty since we're using free input
                value={elements}
                inputValue={inputValue}
                onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                onChange={(_, newValue) => {
                  newValue = newValue.filter((v) => v.trim() !== ''); // filter out empty strings
                  setElements(newValue);
                  if (newValue.length === 0) {
                    setElementsError('At least one element is required');
                  } else {
                    setElementsError(null);
                  }
                }}
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => {
                    const tagProps = getTagProps({ index });
                    const { key, ...rest } = tagProps;

                    return (
                      <Chip
                        key={key} // explicitly pass it
                        label={option}
                        variant="outlined"
                        {...rest}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    key="element-input" // avoids MUI key warning on dynamic resets
                    variant="outlined"
                    label=""
                    placeholder="Press Enter to add"
                    onBlur={() => {
                      const trimmed = inputValue.trim();
                      if (trimmed && !elements.includes(trimmed)) {
                        setElements([...elements, trimmed]);
                        setElementsError(null);
                      }
                      setInputValue('');
                    }}
                    error={!!elementsError}
                    helperText={elementsError}
                  />
                )}
              />
            </Box>
            <TextField
              fullWidth
              label="Year"
              type="number"
              value={year}
              margin="normal"
              onChange={(e) => setYear(parseInt(e.target.value) || 0)}
            />
            <TextField fullWidth label="DOI" value={doi} margin="normal" onChange={(e) => setDoi(e.target.value)} />
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
        <Button
          onClick={() =>
            validateFields() &&
            handleSave({
              id: editingItem?.id ?? -1,
              mainAuthor: author,
              elements: elements.join(','),
              year: year,
              doi: doi,
              simulationMethod: editingItem?.simulationMethod ?? '',
            })
          }
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
